import { BackIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Slider } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { FloatComparator, IntComparator, SearchDetail } from '@generated/graphql';
import { Divider, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { FilterComponent } from './types';

export const NumberRangeFilter: FilterComponent<IntComparator[] | FloatComparator[]> = ({
  title,
  filter,
  details,
  onApply,
  onBack,
}) => {
  if (filter.type !== 'numberRange') {
    throw new Error('NumberRangeFilter does not support this filter type');
  }

  const value = filter.value?.[0];

  const [dirtyMinValue, setDirtyMinValue] = useState(() =>
    value?.gte
      ? details.findIndex((option: SearchDetail) => option.value === value?.gte.toString())
      : 0,
  );
  const [dirtyMaxValue, setDirtyMaxValue] = useState(() =>
    value?.lte
      ? details.findIndex((option: SearchDetail) => option.value === value?.lte.toString())
      : details.length - 1,
  );

  const marks = useMemo(() => {
    if (!details) return [];
    return details
      .map((option, index) => ({
        value: index,
        label: option.value,
      }))
      .sort((a, b) => a.value - b.value);
  }, [details]);

  return (
    <Stack
      maxHeight="500px"
      minWidth={`${200 + Math.max(details ? details.length - 3 : 0, 0) * 15}px`}
    >
      <Group alignItems="center" gap={1} sx={{ px: 2 }}>
        <IconButton size={16} onClick={onBack} Icon={BackIcon} />
        <Typography variant="body2">{title}</Typography>
      </Group>
      <Divider sx={{ m: 2 }} flexItem />
      <Stack sx={{ p: 4 }}>
        <Slider
          min={0}
          max={details ? details.length - 1 : 0}
          value={[dirtyMinValue, dirtyMaxValue]}
          marks={marks}
          step={null}
          sx={{ width: '100%' }}
          onChange={(e, newValue) => {
            setDirtyMinValue(newValue[0] as number);
            setDirtyMaxValue(newValue[1] as number);
          }}
        />
      </Stack>
      <Divider sx={{ m: 2 }} flexItem />
      <Group justifyContent="flex-end" sx={{ px: 2, gap: 2 }}>
        <Button
          size="small"
          variant="secondary"
          onClick={() => {
            setDirtyMinValue(0);
            setDirtyMaxValue(details ? details.length - 1 : 0);
          }}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="primary"
          onClick={() => {
            onApply({
              value: [
                {
                  gte: details?.[dirtyMinValue]?.value
                    ? Number(details?.[dirtyMinValue]?.value)
                    : 0,
                },
                {
                  lte: details?.[dirtyMaxValue]?.value
                    ? Number(details?.[dirtyMaxValue]?.value)
                    : 0,
                },
              ],
            });
          }}
        >
          Apply
        </Button>
      </Group>
    </Stack>
  );
};
